import approveIcon from '@/assets/images/warranty-approve-icon.jpg';
import { useConfirm, useDestructiveAction } from '@/components/feedback/dialog';

import { WARRANTY_CLAIM_APPROVED_MESSAGE } from '../constants';

import { useApproveWarrantyClaim } from './hooks';
import styles from './use-warranty-approve-action.module.scss';

/**
 * Approve orchestration shared by the row kebab and the detail-drawer footer — the single code path
 * README AC1 asks for ("both entry points go through one shared orchestrator"). Mirrors the shipped
 * `useDiscountStatusAction`: a `useConfirm()` dialog with AC-pinned copy (Figma `366:309695`), the
 * POST inside `onConfirm` (so the Approve button shows loading until it settles) and `run` for the
 * success/error toast.
 *
 * Approving records the decision only — **no Order and no Installation** are created (grilling
 * decision 15), so there is no assignee step, no follow-up navigation and no input to collect. That
 * is also why this is a hook rather than a `warranty-action-modals` component: approve renders
 * nothing of its own, while Reject needs a form and keeps its `<RejectWarrantyModal>`.
 */
export function useWarrantyApproveAction() {
  const { confirm } = useConfirm();
  const { run } = useDestructiveAction();
  const approve = useApproveWarrantyClaim();

  // Returns void, not the confirm promise, so plain `onClick`/`onApprove` callers stay clean.
  return (id: string) => {
    void confirm({
      title: 'Approve Warranty Claim?',
      subtitle: 'Are you sure you want to approve this warranty claim?',
      confirmText: 'Approve',
      // The dialog's default illustration is the delete bin; Figma 366:309703 draws this one.
      icon: <img src={approveIcon} alt="" className={styles.icon} />,
      onConfirm: async () => {
        await run(() => approve.mutateAsync(id), {
          success: WARRANTY_CLAIM_APPROVED_MESSAGE,
          errorFallback: 'Could not approve the claim. Please try again.',
        });
      },
    });
  };
}
